import axios from "axios"
import dayjs from "dayjs"
import {omit} from "lodash"

import {disconnectUser} from "../actions/sessionStorage/user/userActions"
import {CONNECT_USER_SUCCESS} from "../actions/sessionStorage/user/userActionTypes"
import {APPLICATION_UNAVAILABLE_URL} from "../constants/links"
import {getServiceApiUrl} from "./apiUrl"
import {navigateTo} from "./navigate"

export const serviceApi = axios.create({
    baseURL: getServiceApiUrl(),
    withCredentials: true,
})

export const addTokenServiceAPIToHeader = token => {
    serviceApi.defaults.headers.common["Authorization"] = token
}

export const removeTokenServiceAPIFromHeader = () => {
    serviceApi.defaults.headers.common = omit(serviceApi.defaults.headers.common, "Authorization")
}

/**
 * Configure axios global variables.
 */
const configureServiceApiAxios = (
    store,
    getExpirationDate,
    getUserToken,
    pagesArchitectures,
    pages,
    getPermissionsSelector,
) => {
    serviceApi.interceptors.request.use(config => {
        const expirationDate = getExpirationDate(store.getState())
        if (expirationDate) {
            const date = dayjs.utc(expirationDate, "DD/MM/YYYY HH:mm:ss", true)
            if (!date.isValid() || date.isBefore(dayjs.utc())) {
                store.dispatch(disconnectUser())
                return {
                    ...config,
                    headers: {
                        ...config.headers,
                        common: {
                            ...omit(config.headers.common, "Authorization"),
                        },
                    },
                }
            }
        }
        return config
    })

    serviceApi.interceptors.response.use(
        response => {
            const newToken = response.headers["x-auth-token"]
            const newExpirationDate = response.headers["x-auth-token-expires-at"]
            if (newToken && getUserToken(store.getState())) {
                addTokenServiceAPIToHeader(newToken)
                store.dispatch({
                    type: CONNECT_USER_SUCCESS,
                    response: {
                        token: newToken,
                        expirationDate: newExpirationDate,
                        permissions: getPermissionsSelector(store.getState()),
                    },
                })
            }
            return response
        },
        error => {
            if (error.response?.status === 401) {
                store.dispatch(disconnectUser())
                navigateTo(pagesArchitectures[pages.LOGIN].route)
            } else if (error.response?.status === 503) {
                window.location.href = APPLICATION_UNAVAILABLE_URL
                return new Promise(() => {})
            }
            if (
                error.request?.responseType === "blob" &&
                error.response?.data instanceof Blob &&
                error.response.data.type?.toLowerCase().includes("json")
            ) {
                return new Promise((resolve, reject) => {
                    const reader = new FileReader()
                    reader.onload = () => {
                        error.response.data = JSON.parse(reader.result)
                        resolve(Promise.reject(error))
                    }
                    reader.onerror = () => reject(error)
                    reader.readAsText(error.response.data)
                })
            }
            return Promise.reject(error)
        },
    )

    const token = getUserToken(store.getState())
    if (token) {
        addTokenServiceAPIToHeader(token)
    }

    return {serviceApi}
}

export default configureServiceApiAxios
